// Meet Levi · V-P2 static modernization · Feb 2026.
//
// Product Owner-authorized visual modernization slice:
//   • Approved static hero artwork (Levi in JobClose AI cap + blue
//     collar + silver "Levi" tag, at his contractor workspace with
//     laptop + notepad + JobClose AI mug + blueprint + tape measure)
//     replaces the previous animation-era Lottie placeholder frame.
//     Asset lives at /brand/meet-levi/hero_levi_workspace_v1.png and
//     is used as supplied — never regenerated, never reinterpreted.
//   • Removed Lottie/animation carryover — the placeholder chip, the
//     "Replay introduction" pill, the imperative CSS-keyframe replay
//     handler, and the "Introduction plays on your first meaningful
//     visit…" caveat paragraph. All other Meet Levi behavior is
//     preserved verbatim.
//   • "Put Levi to Work" red button behavior UNCHANGED — still calls
//     primeTts("meet-levi-put-to-work") for iOS TTS unlock, then
//     openDrawer() into the unified Levi drawer (LEVI-UNIFIED-01).
//     One-Levi architecture untouched.
//   • Added a non-interactive "Try asking me" example strip. These
//     are educational chips — visually obvious as examples, NOT
//     buttons. No onClick, no href, no drawer prefill, no new prompt
//     architecture, no LeviLauncher touch. See lib/leviIntro.js
//     `LEVI_TRY_ASKING` / `LEVI_TRY_ASKING_ES`.
//   • Demo Account banner is NOT rendered here. It is owned by
//     DashboardLayout and conditional on `user.is_demo` — untouched.
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLeviDrawer } from "@/components/LeviLauncher";
import { useLang } from "@/context/LangContext";
import { primeTts } from "@/lib/leviSpeech";
import { Zap, MessageCircleQuestion, ListChecks, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";
import {
  LEVI_INTRO_DOES, LEVI_INTRO_DOES_ES,
  LEVI_INTRO_DOES_NOT, LEVI_INTRO_DOES_NOT_ES,
  LEVI_TRY_ASKING, LEVI_TRY_ASKING_ES,
} from "@/lib/leviIntro";

export default function MeetLevi() {
  const { lang } = useLang();
  const es = lang === "es";
  const pageTitle = es ? "Conocer a Levi" : "Meet Levi";
  return (
    <DashboardLayout title={pageTitle}>
      <MeetLeviBody />
    </DashboardLayout>
  );
}

function MeetLeviBody() {
  const { lang } = useLang();
  const { openDrawer } = useLeviDrawer();
  const es = lang === "es";

  const does    = es ? LEVI_INTRO_DOES_ES    : LEVI_INTRO_DOES;
  const notDoes = es ? LEVI_INTRO_DOES_NOT_ES : LEVI_INTRO_DOES_NOT;
  const prompts = es ? LEVI_TRY_ASKING_ES     : LEVI_TRY_ASKING;

  const introHeadline = es ? "Hola, soy Levi." : "Hi, I'm Levi.";
  const introBody = es
    ? "Piensa en mí como tu compañero de trabajo aquí en JobClose AI. Dame un trabajo, pídeme que encuentre algo o dime en qué necesitas ayuda. Yo te ayudo a mover el trabajo, y tú tomas las decisiones."
    : "Think of me as your coworker here at JobClose AI. Give me a job to work on, ask me to find something, or tell me what you need help with. I'll help you move the work forward, and you make the decisions.";
  const doesLabel = es ? "Lo que Levi puede hacer" : "What Levi can do";
  const notLabel  = es ? "Lo que Levi no hará"    : "What Levi won't do";
  const tagline   = es
    ? (<>Tu negocio. Tus precios. <span className="text-[var(--jc-blue)]">Impulsado por Levi.</span></>)
    : (<>Your business. Your pricing. <span className="text-[var(--jc-blue)]">Powered by Levi.</span></>);
  const putToWork   = es ? "Poner a Levi a trabajar" : "Put Levi to Work";
  const openAria    = es ? "Abrir Levi" : "Open Levi";
  const heroAlt     = es
    ? "Levi con su gorra aprobada de JobClose AI y su placa Levi, sentado en su escritorio de contratista con laptop, cuaderno, taza JobClose AI, cinta métrica y plano."
    : "Levi wearing his approved JobClose AI cap and silver Levi tag, at his contractor workspace with a laptop, notepad, JobClose AI mug, tape measure, and blueprint.";
  const tryAskingHeading = es ? "Prueba preguntándome:" : "Try asking me:";
  const tryAskingHint    = es
    ? "Ejemplos — abre a Levi con el botón rojo para hablar o escribir."
    : "Examples — open Levi with the red button to speak or type.";

  // One-Levi entry — preserved behavior:
  //   1. primeTts unlocks iOS Safari TTS (idempotent per tab).
  //   2. openDrawer() resolves to initialMode="command" via
  //      LEVI-UNIFIED-01 default (see LeviLauncher.jsx L67-75).
  const openLevi = () => {
    primeTts("meet-levi-put-to-work");
    openDrawer();
  };

  return (
      <div data-testid="meet-levi-page" className="max-w-6xl mx-auto">
        {/* Hero — approved artwork as ONE full-width contractor
            workspace scene. Native HTML copy overlays the intentional
            clean/open wall portion on the right side of the image.
            No split image-card + white-column composition. */}
        <section
          className="relative rounded-2xl overflow-hidden ring-1 ring-black/5 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)]"
          data-testid="meet-levi-hero"
        >
          {/* Desktop: the artwork spans the entire hero. Object-position
              `left center` keeps Levi anchored on the left as the
              viewport widens; the intentionally-clean right half of the
              scene grows into a canvas for the HTML overlay. */}
          <img
            src="/brand/meet-levi/hero_levi_workspace_v1.png"
            alt={heroAlt}
            data-testid="meet-levi-page-portrait"
            className="hidden lg:block w-full h-auto select-none"
            draggable={false}
          />
          {/* Mobile: same artwork rendered inline (unified scene stays
              visible above the copy) — CSS below stacks copy under it
              rather than overlaying, so Levi never gets covered. */}
          <img
            src="/brand/meet-levi/hero_levi_workspace_v1.png"
            alt={heroAlt}
            className="lg:hidden w-full h-auto select-none"
            draggable={false}
          />
          {/* Overlay layer — desktop only. Sits on top of the artwork,
              constrained to the right half so it lands in the clean
              open wall area and never covers Levi, the mug, the
              notebook, the laptop, the blueprint, or the framed art. */}
          <div className="hidden lg:flex absolute inset-0 items-center justify-end pointer-events-none">
            <div
              className="w-[46%] max-w-[560px] mr-[3%] -translate-y-16 pointer-events-auto"
              data-testid="meet-levi-hero-copy"
            >
              <button
                type="button"
                onClick={openLevi}
                aria-label={openAria}
                data-testid="meet-levi-character-btn"
                className="block text-left focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] rounded-md"
              >
                <h1 className="font-display text-4xl xl:text-5xl leading-tight text-[var(--jc-text)]" data-testid="meet-levi-headline">
                  {introHeadline}
                </h1>
              </button>
              <p className="mt-4 text-base xl:text-lg leading-relaxed text-[var(--jc-text)]" data-testid="meet-levi-body">
                {introBody}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={openLevi}
                  data-testid="meet-levi-put-to-work-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-5 py-3 text-sm font-semibold hover:bg-red-700 shadow-lg"
                >
                  <Zap size={14} /> {putToWork}
                </button>
              </div>
            </div>
          </div>
          {/* Mobile-only stacked copy — sits directly below the
              artwork, matching the same visual language without
              overlaying Levi at narrow widths. */}
          <div className="lg:hidden px-5 py-6 bg-white" data-testid="meet-levi-hero-copy-mobile">
            <h1 className="font-display text-3xl sm:text-4xl leading-tight text-[var(--jc-text)]">
              {introHeadline}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-[var(--jc-text)]">
              {introBody}
            </p>
            <div className="mt-5">
              <button
                type="button"
                onClick={openLevi}
                data-testid="meet-levi-put-to-work-btn-mobile"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-5 py-3 text-sm font-semibold hover:bg-red-700 shadow-lg"
              >
                <Zap size={14} /> {putToWork}
              </button>
            </div>
          </div>
        </section>

        {/* Capability & safety cards — approved semantic hierarchy.
            BLUE = What Levi CAN do (positive capability).
            RED  = What Levi WON'T do (safety / contractor control). */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <section
            className="jc-card p-6 border-l-4 border-[var(--jc-blue)] bg-[var(--jc-blue)]/5"
            data-testid="meet-levi-does-card"
          >
            <div className="overline mb-3 flex items-center gap-2 text-[var(--jc-blue-700)]">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--jc-blue)] text-white">
                <ListChecks size={13} strokeWidth={2.6} />
              </span>
              {doesLabel}
            </div>
            <ul className="space-y-2 text-sm">
              {does.map((line, i) => (
                <li
                  key={line}
                  data-testid={`meet-levi-does-${i}`}
                  className="flex items-start gap-2"
                >
                  <CheckCircle2 size={14} strokeWidth={2.4} className="text-[var(--jc-blue)] shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>
          <section
            className="jc-card p-6 border-l-4 border-red-600 bg-red-50"
            data-testid="meet-levi-does-not-card"
          >
            <div className="overline mb-3 flex items-center gap-2 text-red-700">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white">
                <ShieldAlert size={13} strokeWidth={2.6} />
              </span>
              {notLabel}
            </div>
            <ul className="space-y-2 text-sm">
              {notDoes.map((line, i) => (
                <li
                  key={line}
                  data-testid={`meet-levi-does-not-${i}`}
                  className="flex items-start gap-2"
                >
                  <XCircle size={14} strokeWidth={2.4} className="text-red-600 shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 font-display text-lg" data-testid="meet-levi-tagline">
              {tagline}
            </p>
          </section>
        </div>

        {/* Non-interactive "Try asking me" example strip.
            These are visual examples of what a contractor can ask
            Levi — NOT buttons. They intentionally have no onClick,
            no href, no role="button", no aria-pressed, and no
            tabindex so screen readers announce them as static text.
            The red "Put Levi to Work" button above remains the ONE
            entry point into the unified Levi drawer. */}
        <section
          className="jc-card mt-10 p-6"
          data-testid="meet-levi-try-asking"
          aria-label={tryAskingHeading}
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] inline-flex items-center justify-center">
              <MessageCircleQuestion size={20} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="overline mb-2">{tryAskingHeading}</div>
              <div className="flex flex-wrap gap-2" data-testid="meet-levi-try-asking-list">
                {prompts.map((p, i) => (
                  <span
                    key={p}
                    data-testid={`meet-levi-try-asking-${i}`}
                    className="inline-block px-3 py-1.5 rounded-full border border-dashed border-[var(--jc-border-strong)] bg-[var(--jc-bg)] text-sm text-[var(--jc-text)] italic"
                  >
                    &ldquo;{p}&rdquo;
                  </span>
                ))}
              </div>
              <p className="mt-3 text-xs text-[var(--jc-text-soft)]" data-testid="meet-levi-try-asking-hint">
                {tryAskingHint}
              </p>
            </div>
          </div>
        </section>
      </div>
  );
}
